// ---------------------------------------------------------------------------
// @storylet-studio/model - the shape source-of-truth.
//
// Transcribes design/storylets-schema.md (bundle, save) and
// design/storylets-source.md (shards). Entity shapes are generic over their
// expression representation E: source shards use plain `src` strings
// (Card<string>), the compiled bundle uses { src, ast } envelopes
// (Card<Expression>). No behaviour lives here.
// ---------------------------------------------------------------------------

import type { Expression, ScalarValue } from "@wildwinter/expr";
import { gameIdify, isValidGameId } from "@wildwinter/toolkit";

export type { Expression, ScalarValue, AstNode } from "@wildwinter/expr";

// --- shared declarations -----------------------------------------------------

export type PropertyType = "boolean" | "number" | "string" | "enum" | "flags" | "quality";

/** A property declaration: @world / @story / @box / @deck / tag / hand
 *  state. A declared property always has a value; referencing an undeclared
 *  property is a publish-time error. */
export interface PropertyDecl {
  name: string;
  type: PropertyType;
  /** Optional in source: left out, the type's default holds (false, 0, "",
   *  the first enum value, no flags, the first stage), by the kernel's
   *  `defaultFor`. The compiler writes the resolved default onto every
   *  declaration in the bundle, so a bundle's declaration always has one. */
  default?: ScalarValue;
  values?: string[];
  /**
   * A quality's ordered ladder of stage names (design/quality.md). Order IS
   * the meaning: `>=` compares by position here, and `advance()` steps along
   * it. The one order-semantic list in the format, accepted as such: it is a
   * declaration, and inserting a stage mid-ladder is the design's whole point.
   */
  stages?: string[];
  /**
   * Host scopes only (`@world`, `@player`, ...; see `HostScopeDecl`). `false`
   * makes the property read-only TO THE STORY: a condition may read it, an
   * outcome that writes it is a compile error. The game still moves it through
   * its resolver; this is the story's statement of intent, not the game's
   * policy. Mirrors Patter's `HostScopeDecl.writable` name for name (Reboot.md
   * 10, ruled 2026-09-03). Ignored on every other scope. Absent = writable.
   */
  writable?: boolean;
  /**
   * The sharing axis (design/flows.md, Patter's flag adopted): is this
   * property's value one world value across all flows, or a copy per flow?
   * It does NOT change reference syntax - sharing is set here, on the
   * declaration, not by a different scope token. Absent = the scope
   * default: `@story` shared; box, deck, hand and tag properties per-flow.
   * On a `@world` declaration the flag is a validation error - `@world` is
   * the game's own state, always engine-level, never per-flow.
   */
  shared?: boolean;
  /**
   * The durability axis (design/engine-server.md 4.2), valid wherever `shared`
   * is valid and orthogonal to it: `shared` says whose value this is WITHIN a
   * run, `durable` says whether the value survives the run at all. A durable
   * shared property is the installation's memory ("trolls defeated since we
   * opened"); a durable per-flow one is the player's pocket (visits,
   * allegiance, what they earned).
   *
   * The engine partitions by `shared` alone; this flag only decides what the
   * durable halves carry across a run boundary: `engine.saveDurable()` and
   * `loadDurable` for a shared one, `flow.saveDurable()` and `openFlow`'s
   * `durable` for a per-flow one.
   *
   * On a `@world` declaration the flag is a validation error, for the reason
   * `shared` is: @world is the game's own state, and how long the game keeps it
   * is the game's business.
   */
  durable?: boolean;
  purpose?: string;
}

/**
 * A property of a host scope (`@world`, `@player`, ...): the game's own state,
 * declared by the project. The same shape as Patter's `HostScopeDecl` and the
 * `@wildwinter/scoperegistry` `ScopeDeclaration`. `default` seeds the
 * standalone engine's self-backed bag; `writable: false` makes the property
 * read-only to the story (an outcome may not write it), never to the game.
 */
export interface HostScopeDecl {
  name: string;
  type: PropertyType;
  values?: string[];
  /** For quality: the ordered stage ladder (see `PropertyDecl.stages`). */
  stages?: string[];
  default?: ScalarValue;
  writable?: boolean;
  /** Free-text author note documenting the property (authoring only). */
  purpose?: string;
}

/** One scope (`token`) in a project's host-scope registry. Patter's shape. */
export interface HostScopeSpec {
  /** The scope token after `@` (`"world"`, `"player"`). Never one of the
   *  Storylet Engine's own (`story`, `box`, `deck`, `hand`). */
  token: string;
  /** Scope-level read/write default for its declarations (default true). */
  writable?: boolean;
  /** Property declarations; omit for an opaque scope (any name, unchecked). */
  declarations?: HostScopeDecl[];
}

/** A project's host-scope registry: every scope the game provides itself.
 *  Structurally scoperegistry's `ScopeRegistrySpec`, and Patter's. */
export interface HostScopeRegistry {
  version: number;
  scopes: HostScopeSpec[];
}

/** The `world` block of a bundle @1 or @0, and of a project shard before
 *  host scopes: @world's declarations, the one game scope there was. */
export interface LegacyWorldBlock {
  properties: PropertyDecl[];
  /** Never read. */
  registry?: unknown;
}

/**
 * The host scopes a bundle or a project shard declares, in order: its
 * `scopeRegistry`'s, or, for a bundle @1 or @0 and a project not yet
 * formatted, its `world` block read as the scope `world` (none when that block
 * declares nothing, which is what an empty `world` always meant).
 */
export function bundleHostScopes(of: { scopeRegistry?: HostScopeRegistry; world?: LegacyWorldBlock }): HostScopeSpec[] {
  if (of.scopeRegistry !== undefined) return of.scopeRegistry.scopes;
  const legacy = of.world?.properties;
  return legacy !== undefined && legacy.length > 0 ? [{ token: "world", declarations: legacy }] : [];
}

/** One host scope by token, case-sensitive, from `bundleHostScopes`. */
export function hostScopeSpec(of: { scopeRegistry?: HostScopeRegistry; world?: LegacyWorldBlock }, token: string): HostScopeSpec | undefined {
  return bundleHostScopes(of).find((s) => s.token === token);
}

/** A host scope's declarations by token (empty when it declares none, is
 *  opaque, or isn't there). `hostScopeDecls(bundle, "world")` is what
 *  `bundle.world.properties` was. */
export function hostScopeDecls(of: { scopeRegistry?: HostScopeRegistry; world?: LegacyWorldBlock }, token: string): HostScopeDecl[] {
  return hostScopeSpec(of, token)?.declarations ?? [];
}

/**
 * A project shard with one host scope's declarations replaced (the scope
 * added, last, when it is new), in place: the legacy `world` block moves into
 * `scopeRegistry` first, so a shard is never written with both. The scope's
 * own `writable` is kept.
 */
export function setHostScopeDecls(shard: { scopeRegistry?: HostScopeRegistry; world?: LegacyWorldBlock }, token: string, declarations: HostScopeDecl[]): void {
  migrateLegacyWorld(shard);
  const registry = shard.scopeRegistry ?? { version: 1, scopes: [] };
  const at = registry.scopes.findIndex((s) => s.token === token);
  if (at >= 0) registry.scopes[at] = { ...registry.scopes[at]!, declarations };
  else registry.scopes.push({ token, declarations });
  shard.scopeRegistry = registry;
}

/**
 * Move a project shard's legacy `world` block into `scopeRegistry`, in place
 * (what `storyletengine format` does): `world.properties` becomes the scope
 * `world`, its `shared` and `durable` flags dropped, since neither means
 * anything on a game's scope. An empty block just goes. Returns whether
 * anything moved.
 */
export function migrateLegacyWorld(shard: { scopeRegistry?: HostScopeRegistry; world?: LegacyWorldBlock }): boolean {
  if (shard.world === undefined) return false;
  const properties = shard.world.properties ?? [];
  delete shard.world;
  if (properties.length === 0 || shard.scopeRegistry?.scopes.some((s) => s.token === "world")) return true;
  const declarations: HostScopeDecl[] = properties.map((d) => {
    const { shared: _shared, durable: _durable, ...rest } = d;
    return rest;
  });
  shard.scopeRegistry = {
    version: shard.scopeRegistry?.version ?? 1,
    scopes: [{ token: "world", declarations }, ...(shard.scopeRegistry?.scopes ?? [])],
  };
  return true;
}

/**
 * A field's type: the family's one list, shared with Patter's game data
 * (patterkit design/family-parity-2026-10.md item 9). `text` is the spelling;
 * `string` is read as a synonym, the spelling fields had before 2026-10, and
 * means exactly what `text` does. `multiline` is text a writer expects to run
 * to more than one line. A `quality` field is a stage of its own ladder
 * (`stages`), compared by position by a host, never by the engine.
 */
export type FieldType = "text" | "multiline" | "string" | "number" | "boolean" | "enum" | "flags" | "quality";

/** Every field type, in the order an editor offers them (`string` left out:
 *  it is read, never offered). */
export const FIELD_TYPES: readonly FieldType[] = ["text", "multiline", "number", "boolean", "enum", "flags", "quality"];

/** Is this field type text of either shape (`text`, `multiline`, `string`)? */
export const isTextFieldType = (type: string): boolean => type === "text" || type === "multiline" || type === "string";

/** A template field (box-defined), of the card template or of the outcome
 *  fields. Data for the host; the engine never interprets fields and they are
 *  not addressable from expressions. The bundle carries the declaration as
 *  written, `string` included. */
export interface FieldDecl {
  name: string;
  type: FieldType;
  /** Optional in source: left out, the type's default holds, as for a
   *  property, and the compiler writes it into the bundle. When given: for an
   *  enum, one of `values` or `""` (none chosen); for flags, a subset of
   *  `values`; for a quality, one of `stages`. */
  default?: ScalarValue;
  values?: string[];
  /** A quality's ordered ladder, by the family's one rule (at least two
   *  stages, none empty, none twice; `@wildwinter/expr`). */
  stages?: string[];
  purpose?: string;
}

/** Cooldown policy, in turns (schema 3.4). */
export type RedrawPolicy = "always" | "never" | number;

// --- gameId derivation (Patter's effectiveGameId, adopted 2026-07-20) --------
//
// gameId is the renameable host-facing address; it is OPTIONAL in source and
// derived from the entity's title until the author pins one, so a rename of
// the title carries the address with it (no "new-deck" stuck placeholder).
// The compiler fills a concrete gameId into every bundle entity.

// `gameIdify` (a human label slugified into a filename- and address-safe gameId) and
// `isValidGameId` are the family's one rule, shared with Patter from `@wildwinter/toolkit`
// (step 6, Q19), and re-exported here so this package's API is unchanged.
export { gameIdify, isValidGameId };

// --- property names (adopted 2026-08-18, with Patter, from one rule) ---------
//
// Design and argument: `@wildwinter/app-shell` src/property-names.ts. Not house
// style: the rule is what `@wildwinter/expr` can parse. Its lexer takes an
// identifier as /[a-zA-Z_][a-zA-Z0-9_]*/ and folds it to lower case, so
// `@story.isNight` reaches a property called `isnight`, `@story.9lives` and
// `@story.not` are parse errors, and `@story.is-night` is not an error at all: it
// compiles to `@story.is` MINUS the string "night". That last one is why the rule
// is enforced rather than trusted - it is the only violation that silently means
// something else.
//
// Here rather than behind an import of the UI kit because the compiler, the CLI
// and the embedded runtime resolve state by these. `property-name-parity.test.ts`
// holds them to the shell's, and `property-name-grammar.test.ts` holds them to the
// parser they came from.

/** The words `@wildwinter/expr` lexes as keywords, so no property may be called one. */
// A legal property NAME is a fact about the expression language, not about this
// model: `not` is reserved because the tokeniser reads it as an operator. Both
// families kept their own copy of the rule AND of the keyword list, a list
// neither owned. @wildwinter/expr derives the list from its own tokeniser, so a
// keyword added there cannot leave a stale copy here.
//
// Re-exported so nothing that imports them has to move.
export {
  propertyNameify, isValidPropertyName, isCaseOnlyPropertyName, RESERVED_PROPERTY_NAMES,
} from "@wildwinter/expr";
/** The effective address: a pinned gameId, else derived from the title, else
 *  the immutable id (so there is always something addressable). */
export function effectiveGameId(entity: { gameId?: string; title?: string; id: string }): string {
  const pinned = entity.gameId?.trim();
  if (pinned) return pinned;
  const fromTitle = entity.title ? gameIdify(entity.title) : "";
  return fromTitle || entity.id;
}

// --- the value scope's owner segment (design/engine-server.md 4.4) -----------
//
// Every other owned scope names its owner with a gameId that is unique across
// the bundle: box, deck, hand and card. A TAG's gameId is unique only within
// its group, and a group's only within its box, so two boxes may each name a
// tag "docks" - as ordinary as two boxes each having a "zone" group - and
// `value.docks.danger` then names two stores.
//
// So the value scope's owner segment is box-qualified where it has to be:
// `value.<boxGameId>/<tagGameId>.<name>`. The slash sits INSIDE the owner
// segment, so the address still splits into three on the dot and no parser
// changes shape. The qualified form is always accepted; the short form is
// accepted while exactly one tag in the bundle carries that gameId, and
// refused when more do, naming the qualified candidates. What a runtime
// PRINTS - `listProperties`, a write on the trace, a load report, an
// examiner - is the short form except where the gameId repeats.
//
// One definition, because the Board draws these addresses from the bundle
// while the engine builds them from its own index, and an address the editor
// shows that the engine will not take is the fault 4.4 was fixing.

// A ZONE of the project map (design/project-map-contract.md 3.4) is the one
// tag with no qualified form: it belongs to no box, so it prints and is
// accepted as `value.<zoneGameId>.<name>` whichever boxes use it. Nothing can
// make that ambiguous, because no box tag may share a zone's gameId (refused
// by the compiler and again by the engine at load). A box-qualified form that
// names a zone is REFUSED rather than accepted for old times' sake: there is
// no old project to be kind to, and the qualified form would assert a box the
// zone does not have.

/** The answers a value address needs, all derived from the bundle. */
export interface ValueAddresses {
  /** Tag internal id -> the owner segment an address PRINTS for it. */
  print: Map<string, string>;
  /** Every owner segment a value address ACCEPTS -> the tag's internal id.
   *  Holds the qualified form for every box tag, the short form only for a
   *  gameId no other box tag shares, and the short form of every zone. */
  accept: Map<string, string>;
  /** A tag gameId more than one box uses -> its qualified forms, in bundle
   *  order. Empty for the overwhelming majority of projects, and what a
   *  refusal lists. Never holds a zone. */
  repeated: Map<string, string[]>;
  /** A box-qualified segment that names a project-map zone
   *  (`"box/quay"`) -> the zone's own segment (`"quay"`), the one an
   *  address must use instead. One entry per box and zone, so a refusal can
   *  name the address that works without working it out again. Empty when
   *  the bundle has no map. */
  zoneQualified: Map<string, string>;
}

/** The owner segment of every tag in the bundle, both ways round: the boxes'
 *  tags, then the project map's zones. */
export function valueAddresses(bundle: {
  boxes: readonly { id: string; gameId?: string; title?: string; tagGroups: readonly TagGroup[] }[];
  map?: { group: TagGroup };
}): ValueAddresses {
  const tags: { id: string; gameId: string; qualified: string }[] = [];
  for (const box of bundle.boxes) {
    const boxGameId = effectiveGameId(box);
    for (const group of box.tagGroups) {
      for (const tag of group.tags) {
        const gameId = effectiveGameId(tag);
        tags.push({ id: tag.id, gameId, qualified: `${boxGameId}/${gameId}` });
      }
    }
  }
  // Distinct qualified forms per gameId. Distinct rather than a count: two
  // groups in ONE box may also name a tag the same way, and a refusal that
  // offered the same address twice would be no help at all. Those two share
  // the qualified segment, and the first in bundle order answers to it, which
  // is what the short form did for everything before this rule.
  //
  // That last case is closing at the source rather than here (question 16,
  // ruled 2026-09-06): the compiler WARNS that a tag gameId must be unique
  // within its box, across all of that box's groups, and refuses it from the
  // next release. So this stays as the reading rule for a bundle built before
  // that, and a bundle built after it has no repeated qualified form to read.
  const forms = new Map<string, string[]>();
  for (const tag of tags) {
    const list = forms.get(tag.gameId) ?? [];
    if (!list.includes(tag.qualified)) list.push(tag.qualified);
    forms.set(tag.gameId, list);
  }
  const print = new Map<string, string>();
  const accept = new Map<string, string>();
  const repeated = new Map<string, string[]>();
  const zoneQualified = new Map<string, string>();
  for (const tag of tags) {
    const candidates = forms.get(tag.gameId) ?? [tag.qualified];
    const ambiguous = candidates.length > 1;
    print.set(tag.id, ambiguous ? tag.qualified : tag.gameId);
    if (!accept.has(tag.qualified)) accept.set(tag.qualified, tag.id);
    if (!ambiguous && !accept.has(tag.gameId)) accept.set(tag.gameId, tag.id);
    if (ambiguous) repeated.set(tag.gameId, candidates);
  }
  // The zones, after the box tags and never in `repeated`. A zone whose
  // gameId a box tag also uses is a bundle the engine refuses at load; should
  // one reach here anyway (the Board reads bundles the engine never loaded),
  // the box tag keeps the short form it already had and the zone does not
  // take it over silently.
  for (const tag of bundle.map?.group.tags ?? []) {
    const gameId = effectiveGameId(tag);
    print.set(tag.id, gameId);
    if (!accept.has(gameId)) accept.set(gameId, tag.id);
    for (const box of bundle.boxes) zoneQualified.set(`${effectiveGameId(box)}/${gameId}`, gameId);
  }
  return { print, accept, repeated, zoneQualified };
}

/** What a box-qualified address naming a project-map zone is told: why the
 *  form is wrong, and the address that works (design/project-map-contract.md
 *  3.4). */
export function zoneQualifiedValueAddressMessage(segment: string, zone: string, name: string): string {
  return `"value.${segment}.${name}": "${zone}" is a zone of the project map, which belongs to no box; `
    + `write "value.${zone}.${name}"`;
}

/** What an ambiguous short-form value address is told: the candidates, in
 *  full, because "that names two tags" without them leaves a host reading a
 *  bundle it did not write to find out which boxes. */
export function ambiguousValueAddressMessage(
  segment: string, name: string, candidates: readonly string[],
): string {
  const forms = candidates.map((q) => `"value.${q}.${name}"`);
  const list = forms.length <= 1 ? (forms[0] ?? "")
    : `${forms.slice(0, -1).join(", ")} or ${forms[forms.length - 1]}`;
  return `"value.${segment}.${name}" names a tag in ${candidates.length} boxes; write ${list}`;
}

/**
 * The first free gameId of the form `base`, `base-2`, `base-3`, ... not already
 * in `taken`.
 *
 * A gameId is API - `deal()` and the play log speak it - so a name minted for a
 * new or duplicated entity must not collide with an existing one. This lived in
 * two copies, one in the editor and one in the CLI's kit scaffolder, character
 * for character the same; two copies of an addressing rule can drift, and a
 * drift here means the same act produces different addresses depending on which
 * program did it. It is here, beside `gameIdify`, because both programs need it
 * and no UI touches it.
 */
export function freeGameId(base: string, taken: ReadonlySet<string>): string {
  let gameId = base;
  for (let n = 2; taken.has(gameId); n++) gameId = `${base}-${n}`;
  return gameId;
}

/**
 * The first free TITLE of the form `base`, `base 2`, `base 3`, ... whose
 * derived gameId is not already in `taken`.
 *
 * The sibling of `freeGameId` for the "New box", "New deck" case, where the
 * author is given a title and the address follows from it. The dedupe is on the
 * DERIVED gameId rather than the title, because two titles that slug to one
 * address are the collision that matters.
 */
/**
 * An id-sorted collection in the order a person should SEE it.
 *
 * Storage is sorted by immutable id (source rule 5) so that two authors adding
 * one item each never touch the same line. That makes array position useless as
 * display order, so the order the author arranged rides in a sparse `order`
 * field, with position as the fallback and id to break a tie.
 *
 * One definition, because this rule has to give the same answer in four places
 * that a reader compares side by side: the compiler (what the bundle carries),
 * the editor (what the card document lists), the exports, and Find. When they
 * disagree, the editor shows one order and the game plays another.
 */
export function byDisplayOrder<T extends { id?: string; order?: number }>(items: readonly T[]): T[] {
  return items
    .map((x, i) => ({ x, key: x.order ?? i, i }))
    .sort((a, b) => a.key - b.key || ((a.x.id ?? "") < (b.x.id ?? "") ? -1 : (a.x.id ?? "") > (b.x.id ?? "") ? 1 : a.i - b.i))
    .map((e) => e.x);
}

export function freeTitle(base: string, taken: ReadonlySet<string>): string {
  let title = base;
  for (let n = 2; taken.has(gameIdify(title)); n++) title = `${base} ${n}`;
  return title;
}

/**
 * A count of a TIMED box's turns, said as time (design/engine-server.md 4.8):
 * `turnSpan(30, 60)` is "30 min", and `turnSpan(30, 60, true)` is "30 minutes".
 *
 * One definition, because the conversion appears wherever a designer might
 * otherwise have to do it in their head: the card editor's Redraw field, the
 * box page, the Board's advance buttons, and the coverage report's turn
 * budget. Two of those want the unit spelled out and two want it short, which
 * is the whole of `long`.
 */
export function turnSpan(turns: number, seconds: number, long = false): string {
  const total = Math.max(0, Math.round(turns * seconds));
  const say = (n: number, short: string, one: string, many: string): string =>
    long ? `${n} ${n === 1 ? one : many}` : `${n} ${short}`;
  if (total < 60) return long ? say(total, "s", "second", "seconds") : `${total}s`;
  if (total < 3600) {
    const minutes = total % 60 === 0 ? total / 60 : Math.round(total / 6) / 10;
    return say(minutes, "min", "minute", "minutes");
  }
  let hours = Math.floor(total / 3600);
  let rest = Math.round((total % 3600) / 60);
  if (rest === 60) { hours += 1; rest = 0; }
  const said = say(hours, "hr", "hour", "hours");
  return rest === 0 ? said : `${said} ${say(rest, "min", "minute", "minutes")}`;
}

// --- entities (generic over expression representation E) ---------------------

export interface Outcome<E> {
  /** Bundle only: this node's extension values, by extension id, raw as
   *  authored (editor-only fields stripped). Source keeps them in extension
   *  shards, never here. Absent when the node has none. */
  extensions?: NodeExtensions;
  id: string;
  gameId?: string;
  title?: string;
  purpose?: string;
  /** Authored display order (sparse; one without it falls back to its id
   *  position). Unlike `Card.order` this one IS compiled into the bundle:
   *  which option is offered first is authorial, and a host reading a dealt
   *  card's outcomes is building the player's menu. */
  order?: number;
  /** Gating; availability is always evaluated against current state. */
  condition?: E;
  /** Target ("@scope.name") -> expression; all right-hand sides evaluate
   *  against pre-play state (schema 3.7). */
  changes: Record<string, E>;
  /** Template data, as `Card.fields` is: field name -> value, declared by
   *  the box's `outcomeFields`, validated at publish, and handed to the host
   *  with the outcome. The engine never reads it: a press can say one line
   *  ("The notice is in your pocket") without spending a card on it. */
  fields?: Record<string, ScalarValue>;
}

/** A box's defaults for its cards' dealing rules (its Card template). */
export interface CardDefaults {
  redraw?: RedrawPolicy;
  weight?: number;
  inOneHand?: number;
  acrossHands?: "one" | "any";
}

export interface Card<E> {
  /** Bundle only: this node's extension values, by extension id, raw as
   *  authored (editor-only fields stripped). Source keeps them in extension
   *  shards, never here. Absent when the node has none. */
  extensions?: NodeExtensions;
  id: string;
  gameId?: string;
  title?: string;
  purpose?: string;
  /** Authored display order within the deck (sparse; a card without one falls
   *  back to its id position). Merges as a per-card value, so id-sorted storage
   *  stays merge-clean (Reboot 7.4); dropped from the compiled bundle. */
  order?: number;
  condition?: E;
  /** Default 0; an expression must evaluate to a number. */
  priority: number | E;
  /** Always present in a bundle. A source card may leave it out, and then
   *  takes its box's Card template default (or "always"). */
  redraw?: RedrawPolicy;
  /** Tags: tag group id -> tag ids. An absent group is a wildcard (matches
   *  any binding of it), except the reserved home group, whose default
   *  inverts (schema 2.4). Editors and fixtures speak gameIds; stored
   *  references are ids. */
  tags?: Record<string, string[]>;
  /** How often this card comes up beside cards of equal rank: a positive
   *  number, default 1 (the hands-on review, 2026-10: weights in place of
   *  Copies). Priority, then specificity, still decide between ranks; within
   *  a run of equal cards a card of weight 2 is drawn ahead twice as often as
   *  one of weight 1. A run whose weights are all equal shuffles exactly as it
   *  always has. */
  weight?: number;
  /** How many slots of ONE hand this card may fill: integer >= 1, default 1
   *  (an encounter comes up once; a pickup or a piece of equipment can be up
   *  to 3). */
  inOneHand?: number;
  /** Whether several hands may hold this card at once, within a flow:
   *  "one" (the default, a one-off can't be offered in two places) or "any"
   *  (interchangeable filler). Replaces `copies`. */
  acrossHands?: "one" | "any";
  /** @deprecated Read when `acrossHands` is absent, for a bundle from before
   *  it: how many hands may hold this card at once (schema 3.5), integer >= 1,
   *  default 1. `storyletengine format` writes `acrossHands` in its place
   *  (1 is "one", more is "any"). */
  copies?: number;
  /** Scarcity across flows (design/shared-scarcity.md). Absent takes the
   *  deck's flag; set here it overrides the deck, so a single unique card can
   *  stay in the content it belongs to. A shared card's claims count every
   *  flow's board, and a shared `redraw: "never"` is spent for everyone the
   *  first time anyone plays it.
   *
   *  A finite `redraw` stays PER FLOW even when shared: a cooldown is an
   *  absolute turn of the card's box clock and clocks are per flow, so
   *  "3 turns of whose clock?" has no answer. A world-wide timer is a @world
   *  question, not an engine one (shared-scarcity 9.3.3). */
  shared?: boolean;
  /** The world cap: how many hands ACROSS EVERY FLOW may hold this at once.
   *  Read only when the card is shared, and defaults to the per-flow limit
   *  (one hand, or `copies`), so "five in the world, one to a customer" is
   *  `sharedCopies: 5`. */
  sharedCopies?: number;
  /** Does this card's `redraw: "never"` spend survive the run
   *  (design/engine-server.md 4.2)? Absent takes the deck's flag, set here it
   *  overrides the deck, exactly as `shared` does. `shared` decides who a
   *  spend counts for WITHIN a run; this decides whether it outlives one.
   *
   *  Only `"never"` crosses the run boundary, for the reason only `"never"`
   *  crosses the flow boundary (shared-scarcity 9.3.2): a finite cooldown is
   *  an absolute turn of a box clock, and the clock resets with the run. On
   *  any other redraw the flag is a compile warning.
   *
   *  Like the declaration flag, it only decides what the durable halves carry:
   *  a per-flow spend rides the flow's half (its `never` cooldown), a shared
   *  one the engine's half (its spent set), each by card gameId. */
  durable?: boolean;
  /** Card-template data: field name -> value, validated at publish. */
  fields?: Record<string, ScalarValue>;
  outcomes: Outcome<E>[];
}

export interface Deck<E> {
  /** Bundle only: this node's extension values, by extension id, raw as
   *  authored (editor-only fields stripped). Source keeps them in extension
   *  shards, never here. Absent when the node has none. */
  extensions?: NodeExtensions;
  id: string;
  gameId?: string;
  title?: string;
  purpose?: string;
  /** The deck gate, evaluated once per draw in the draw's environment. */
  condition?: E;
  /** This pile is scarce across flows (design/shared-scarcity.md): every card
   *  in it is shared unless the card says otherwise. The container is where
   *  Patter puts its own shared-memory flag, and the deck is our container. */
  shared?: boolean;
  /** Every `redraw: "never"` card in this pile is spent for good, past the end
   *  of the run, unless the card says otherwise (design/engine-server.md 4.2).
   *  The container carries the flag for the reason `shared` is carried here:
   *  a pile is what an author reaches for when a rule is true of all of it. */
  durable?: boolean;
  properties: PropertyDecl[];
  cards: Card<E>[];
}

export interface Tag {
  /** Bundle only: this node's extension values, by extension id, raw as
   *  authored (editor-only fields stripped). Source keeps them in extension
   *  shards, never here. Absent when the node has none. */
  extensions?: NodeExtensions;
  id: string;
  gameId?: string;
  /**
   * This tag's own starting values for properties its GROUP declares
   * (design/hand-typing.md). The group says what the property IS; a tag says
   * only where it starts, so "every zone has a haunting level" is written once
   * and "the cave starts at 2" is written where it belongs.
   *
   * A name here that the group does not declare is an error: it would be a
   * value for nothing.
   */
  values?: Record<string, ScalarValue>;
  /** Authored display order (sparse; one without it falls back to its id
   *  position). Merges as a per-item value, so id-sorted storage stays
   *  merge-clean (Reboot 7.4). */
  order?: number;
  properties?: PropertyDecl[];
  /** Template-of-play extras (e.g. spatial geometry). Source only: preserved
   *  in shards, never compiled into the bundle. */
  templates?: Record<string, unknown>;
}

/** A named axis for cross-cutting cards (schema 2.4, renamed from
 *  Dimension). Tags are declared, not freeform. */
export interface TagGroup {
  /** Bundle only: this node's extension values, by extension id, raw as
   *  authored (editor-only fields stripped). Source keeps them in extension
   *  shards, never here. Absent when the node has none. */
  extensions?: NodeExtensions;
  id: string;
  gameId?: string;
  purpose?: string;
  /**
   * A property reference (`"@story.act"`) whose value names a tag in this group
   * by gameId. The engine reads it at every ask and binds the group, exactly as
   * if the asking hand had chosen that tag; a hand's own binding wins.
   *
   * For an axis driven by STATE rather than by place: acts, chapters, a
   * difficulty band. Without it, only a hand can bind a group, so such an axis
   * had nowhere to gate and every card needed its own condition.
   *
   * A reference rather than an expression on purpose (design/where-and-
   * selectors.md Part B): a computed binding belongs in a property the outcomes
   * maintain, and an expression here would make this type generic for no gain.
   */
  boundBy?: string;
  /**
   * What omitting this group means for a card. Default false: omission is a
   * wildcard, so the card matches whatever the group is bound to. True inverts
   * it, so a card that names no tag here is unavailable wherever the group IS
   * bound (and unaffected where it is not).
   *
   * `place` is the built-in instance of this pair: bound to the asking hand,
   * and inverted per card rather than per group.
   */
  required?: boolean;
  /** Authored display order (sparse; one without it falls back to its id
   *  position). Merges as a per-item value, so id-sorted storage stays
   *  merge-clean (Reboot 7.4). */
  order?: number;
  /**
   * Properties EVERY tag in this group has (design/hand-typing.md). The
   * declaration lives here and each tag carries only its own starting value in
   * `Tag.values`, which is the separation the format was missing: a tag's own
   * `properties` entry has to restate the type on every tag purely in order to
   * say the value, and a tag added later silently arrives without it.
   *
   * Compiled by FLATTENING onto each tag, so the bundle keeps its per-tag
   * shape and no runtime, port or bundle schema changes: source is where the
   * author works and where merges happen, the bundle is a compiled artefact
   * that can afford to be explicit.
   *
   * A tag may still declare its own `properties` for a group whose tags
   * genuinely differ. Declaring the same NAME both ways is an error.
   */
  properties?: PropertyDecl[];
  tags: Tag[];
  /** Template-of-play extras for the GROUP, the same bag its tags carry: this is
   *  where a group is marked spatial and where that template keeps its own
   *  group-level configuration. Source only, preserved but never compiled.
   *
   *  A bag rather than a `spatial: true` flag because the marker and the
   *  configuration are one thing (see model/spatial.ts), and because core is not
   *  meant to grow a field per template of play. */
  templates?: Record<string, unknown>;
}

/** The reserved tag group (schema 2.4): present in every box without
 *  declaration, its tags the box's hand ids. Every hand implicitly binds it to
 *  itself; a card that names a place is available only at that place.
 *
 *  Called `place` rather than `home` since 2026-08-21: one word for one thing
 *  across the format, the editor and the exports. "Where" is the QUESTION a
 *  card answers (at a place, or anywhere in a region); "place" is the direct
 *  half of that answer. `home` was a metaphor an author had to learn, and it
 *  leaked into hand-edited shards and the docs. */
export const PLACE_GROUP = "place";

/** The scopes a movable hole may be filled from (design/engine-server.md 4.6):
 *  the two `boundBy` already allows, plus `@hand` - the asking hand's OWN
 *  declared property, resolved before tag composition so a movable hole can
 *  never depend on the tags it is choosing. */
export type HoleRefScope = "hand" | "story" | "world";

/** A parsed hole reference: `@hand.zone` -> `{ scope: "hand", name: "zone" }`. */
export interface HoleRef {
  scope: HoleRefScope;
  name: string;
}

const HOLE_REF = /^@(hand|world|story)\.([a-z][a-z0-9_-]*)$/;

/**
 * Is this `chosen` / binding value MEANT as a property reference rather than a
 * tag id?
 *
 * The test is the leading `@` alone, deliberately: a value that starts with
 * one and does not parse is a mistyped reference, which the compiler should
 * name as such, not a tag id that happens to look odd. Tag ids never begin
 * with `@`.
 */
export const isHoleRef = (value: string): boolean => value.startsWith("@");

/** Parse a hole reference, or undefined when it is not one. The on-disk form
 *  stays a plain string, so the canonical serialiser and the shard merge need
 *  no change at all: a hole is still one group name against one value. */
export const parseHoleRef = (value: string): HoleRef | undefined => {
  const m = HOLE_REF.exec(value);
  return m === null ? undefined : { scope: m[1] as HoleRefScope, name: m[2]! };
};

/** A declared kind of hand (schema 2.6): live-inherited, author-side only,
 *  never called from game code. One condition governs every instance. */
export interface HandTemplate<E> {
  /** Bundle only: this node's extension values, by extension id, raw as
   *  authored (editor-only fields stripped). Source keeps them in extension
   *  shards, never here. Absent when the node has none. */
  extensions?: NodeExtensions;
  id: string;
  gameId?: string;
  title?: string;
  purpose?: string;
  /** Authored display order (sparse; one without it falls back to its id
   *  position). Merges as a per-item value, so id-sorted storage stays
   *  merge-clean (Reboot 7.4). */
  order?: number;

  /** Fixed tag bindings: tag group id -> tag id. Literal tags only: what a
   *  template FIXES is the same for every instance, and a hole that moves is
   *  the instance's own business (`Hand.chosen`, 4.6). */
  bindings?: Record<string, string>;
  /** The holes: tag group ids each instance fills (one tag each, or one
   *  property reference: 4.6). */
  chooses?: string[];
  /** Shared availability condition, ANDed in (schema 3.1); evaluated per
   *  instance against that instance's composed @hand. */
  condition?: E;
  /** Default slot cap. */
  slots: number | "unbounded";
  /** Declared @hand state every instance carries. */
  properties: PropertyDecl[];
}

/** A standalone hand's inline rule (schema 2.6): owned by the hand. */
export interface HandRule<E> {
  /**
   * Tag group id -> tag id, or a PROPERTY REFERENCE (`"@hand.zone"`,
   * `"@story.where"`, `"@world.place"`) the runtime resolves at ask time
   * (design/engine-server.md 4.6, the hand that moves). Still a plain string
   * on disk, so the canonical serialiser and the merge are untouched; what
   * widened is the meaning, and `parseHoleRef` is where it is read.
   *
   * `place` is never fillable this way: it is the hand's own name, not an axis.
   */
  bindings?: Record<string, string>;
  condition?: E;
  slots: number | "unbounded";
}

/** A hand (schema 2.6): a template instance (template + chosen) or a
 *  standalone hand (rule). Exactly one of template / rule. Fully concrete:
 *  deal is name-only. */
export interface Hand<E> {
  /** Bundle only: this node's extension values, by extension id, raw as
   *  authored (editor-only fields stripped). Source keeps them in extension
   *  shards, never here. Absent when the node has none. */
  extensions?: NodeExtensions;
  id: string;
  /** The name deal() is called with; a rename is a breaking change
   *  (Reboot 7.4). */
  gameId?: string;
  title?: string;
  purpose?: string;
  /** Hand template id (not gameId). */
  template?: string;
  /**
   * Template instances: tag group id -> tag id, one per `chooses` hole.
   *
   * A value may instead be a PROPERTY REFERENCE (`"@hand.zone"`,
   * `"@story.where"`, `"@world.place"`), which makes the hole MOVABLE: the
   * runtime resolves the reference at ask time and binds the hole to the tag
   * the value names, so moving the Elder to the forest is `setProperty` and
   * nothing else (design/engine-server.md 4.6). Still a plain string on disk,
   * so the canonical serialiser and the shard merge need no change; read it
   * with `parseHoleRef`.
   */
  chosen?: Record<string, string>;
  /** Standalone hands: the inline rule. */
  rule?: HandRule<E>;
  /** Override; defaults to the template's / rule's slots. The ONLY template
   *  field an instance may override (schema 2.6). */
  slots?: number;
  /** Standalone hands' own @hand state (template instances inherit the
   *  template's declarations). */
  properties?: PropertyDecl[];
  /** Authored display order within the box (sparse; authoring-only, never
   *  compiled into the bundle - the compiler's explicit field list drops it). */
  order?: number;
  /** Template-of-play extras (e.g. a spatial pin). Source only. */
  templates?: Record<string, unknown>;
}

export interface Box<E> {
  /** Bundle only: this node's extension values, by extension id, raw as
   *  authored (editor-only fields stripped). Source keeps them in extension
   *  shards, never here. Absent when the node has none. */
  extensions?: NodeExtensions;
  id: string;
  gameId?: string;
  title?: string;
  purpose?: string;
  /** The only per-box ranking policy (Reboot 2.2). */
  ranking: { specificity: boolean };
  /**
   * Who moves the box's clock. Absent is the ordinary box, whose clock a
   * play moves. Present, the GAME moves it: with `seconds` it is a TIMED box,
   * counting real time, one turn every `seconds` of the run
   * (design/engine-server.md 4.8); without, it is a calendar or any clock the
   * game ticks on its own beat (the hands-on review, 2026-10: the three-way
   * box clock).
   *
   * Two things follow, and only two. In the ENGINE, a play in this box
   * defaults to advancing nothing: `settings.playAdvancesTurns` does not
   * apply, so a designer cannot declare the convention and then forget to
   * switch play-advance off. Everywhere else it is what the tools SAY: the
   * host ticks the box (the runtime has no clock and gains none here), and a
   * card's `redraw: N` reads as N x `seconds`, which the editors, the bundle
   * inspectors and the coverage report spell out rather than leaving a
   * designer to know that 30 meant minutes.
   *
   * The number itself is inert to the runtime, which never reads it.
   */
  turn?: { seconds?: number };
  /** The card template: what every card in this box carries. */
  fields: FieldDecl[];
  /** What every outcome in this box may carry, declared the same way.
   *  Absent when the box declares none, so a bundle without them is byte for
   *  byte what it was. */
  outcomeFields?: FieldDecl[];
  properties: PropertyDecl[];
  /**
   * The box has opted in to the PROJECT MAP (design/project-map-contract.md
   * 1.2, 2.2): its hands may bind the map's zone group and its cards may be
   * tagged with it, and it sees the group's name beside its own groups' names.
   * Absent is "not on the map", and a box that is not may not reference the
   * group at all: the engine refuses such a bundle at load.
   *
   * Opting in changes nothing about what the box deals FROM: a hand still
   * deals only from its own box's decks. What the boxes on the map share is
   * the zones' values (one bag per zone), never their cards or their history.
   */
  usesMap?: true;
  /** The box's OWN tag groups. The project map's group is never here, even in
   *  a box that uses it: it is the bundle's, in `Bundle.map` (`groupsOfBox`
   *  gives the two together). */
  tagGroups: TagGroup[];
  decks: Deck<E>[];
  handTemplates: HandTemplate<E>[];
  hands: Hand<E>[];
}

// --- the compiled bundle (.storyletsc) ---------------------------------------

/**
 * The bundle schema this build of the format writes: @2 since host scopes
 * (2026-10-28), the bundle whose game scopes are `scopeRegistry` and which has
 * no `world` block. @1 was the project map's (design/project-map-contract.md
 * 2.4), the bundle that can carry `map` and `Box.usesMap`.
 *
 * @2 is a new number because the `world` block is GONE, not added to: a runtime
 * that reads only @1 would reach for `world.properties` and fail as a raw
 * error, where a schema it does not know is refused by name.
 *
 * Every runtime READS @0, @1 and @2 and refuses anything else, by name. Reading @0
 * is not a compatibility branch: an @0 bundle cannot carry a map, so it is the
 * same shape with less in it. The check exists so that from this release on a
 * runtime meeting a schema it does not know says so, rather than half-working
 * the way an @0 runtime does on a map bundle (tag matching is by id, so its
 * deals look right while every zone value is missing).
 */
export const BUNDLE_SCHEMA = "storylets/bundle@2";
/** The project map's schema, before host scopes: the game's scopes were one
 *  `world` block. Still read by every runtime, its `world.properties` as the
 *  host scope `world`. */
export const BUNDLE_SCHEMA_V1 = "storylets/bundle@1";
/** The schema before the project map, still read by every runtime. */
export const BUNDLE_SCHEMA_V0 = "storylets/bundle@0";
/** Every bundle schema a runtime accepts. */
export const BUNDLE_SCHEMAS: readonly string[] = [BUNDLE_SCHEMA_V0, BUNDLE_SCHEMA_V1, BUNDLE_SCHEMA];

/** Binds bundles to shards (staleness gate) and saves to bundles. */
export interface BundleContent {
  project: string;
  version: string;
  /** hash32 over the canonical source shards (schema 2.8). */
  hash: string;
}

export interface BundleSettings {
  playAdvancesTurns: number;
}

/**
 * The play ladder (design/engine-server.md 4.10): how much of itself
 * Storyletter shows this project, in one setting with three rungs rather than
 * a set of toggles, because the features nest.
 *
 *   solo   one player, one flow: no sharing, no durability, no venue features
 *   shared several players over one world: sharing appears
 *   venue  a production: nothing is hidden
 *
 * EDITOR-SIDE ONLY. It stays in the project shard beside `coverage` and
 * `export` and is never compiled: a solo project plays on the same Engine as a
 * venue one. Hidden is hidden rather than disabled, so going DOWN a rung is
 * refused when the project already contains what the rung would hide, and a
 * hand-edited shard above its rung is a compile warning.
 */
export type PlayRung = "solo" | "shared" | "venue";

/** The default rung: a project shard that says nothing is a solo game. */
export const DEFAULT_PLAY_RUNG: PlayRung = "solo";

/** The project shard's settings block: what the bundle carries, plus the
 *  authoring-side play rung that it does not. */
export interface ProjectSettings extends BundleSettings {
  /** The play ladder rung (see `PlayRung`). Absent = "solo". */
  play?: PlayRung;
}

/**
 * The PROJECT MAP (design/project-map-contract.md 2.1): at most one per project,
 * above the boxes. Two halves, on purpose.
 *
 * `group` is SEMANTIC and always ships when the project declares a map: the zone
 * group, compiled exactly as a box's group is (the group's properties flattened
 * onto each tag), which opted-in boxes' hands bind and cards are tagged with BY
 * ID, as with any group. It is in no box's `tagGroups`. Each zone is one tag and
 * so one value bag, whichever boxes' hands are dealt to it.
 *
 * `geometry` is INERT PAYLOAD, as the per-box map block before it was: nothing
 * in the engine reads it, and it is absent unless the project asked for it
 * (`export.map`), so a shipping build carries no shapes. GAME IDS throughout,
 * because a host matches these against the names it passes to `peek`.
 *
 * SITES ARE CONTENT. Until 2026-09-05 they were deliberately left out: a site
 * was where an author parked a hand while working. That held for a game, where a
 * hand's zone is its only real-world meaning; it does not hold for a physical
 * experience (design/engine-server.md 4.3), where the position is where the kiosk
 * stands. They stay per box, because a hand belongs to one box.
 */
export interface ProjectMap {
  /** The zone group. The engine reads this. */
  group: TagGroup;
  /** Drawing data, only under `export.map`. The engine never reads it. */
  geometry?: {
    /** Drawn zones only, by tag gameId, in group order. A tag with no polygon
     *  is not a place yet and is left out rather than shipped as an empty
     *  shape. */
    zones: { tag: string; polygon: ViewPoint[] }[];
    /** Visible background pictures, back to front, as bundle-relative paths. */
    backgrounds?: BundleBackground[];
    /** Box gameId -> where that box's placed hands stand, sorted by hand
     *  gameId so the bytes do not depend on authoring order. Only opted-in
     *  boxes; a box with no site has no key, and no key at all when nothing
     *  is placed. The zone a site sits in is NOT repeated: the hand's own
     *  binding is what the runtime deals from. */
    sites?: Record<string, { hand: string; x: number; y: number }[]>;
  };
}

/** One shipped picture. `locked` and `hidden` are authoring state and do not
 *  travel; the draw order is the array order. */
export interface BundleBackground {
  /** Where the file sits relative to the bundle ("assets/<file>"). */
  file: string;
  x: number;
  y: number;
  width: number;
  height: number;
  opacity?: number;
}

export interface Bundle {
  schema: typeof BUNDLE_SCHEMA | typeof BUNDLE_SCHEMA_V1 | typeof BUNDLE_SCHEMA_V0;
  content: BundleContent;
  metadata: "full" | "stripped";
  settings: BundleSettings;
  /** The game's own scopes the project declares (`@world`, `@player`, ...),
   *  Patter's shape: each one's token, its scope-level `writable` and its
   *  declarations, every default resolved (absent declarations: an opaque
   *  scope). A standalone engine self-backs each from its defaults; a game
   *  binds its own store for any of them (`hostScopes`). Absent when the
   *  project declares none. */
  scopeRegistry?: HostScopeRegistry;
  /** Bundle @1 and @0 only: the one game scope there was, read as the host
   *  scope `world` (see `bundleHostScopes`). A bundle @2 has none. */
  world?: LegacyWorldBlock;
  story: {
    properties: PropertyDecl[];
  };
  boxes: Box<Expression>[];
  /** The project map, when the project declares one (see `ProjectMap`). The
   *  per-box `maps` array it replaces is gone, not kept as a one-element list:
   *  zones are no longer a box's. */
  map?: ProjectMap;
  /** Other engines' game-wide scopes the content names (`patter`), sorted: the
   *  family's shared vocabulary, let through unchecked by the compiler. The
   *  engine reports when the game has not registered one. Absent when none.
   *  A token the project declares (`scopeRegistry`) is the game's, never here. */
  externalScopes?: string[];
  /**
   * The extensions the project takes, by id: each one's version and its
   * compiled declarations by node kind, every default resolved (what a
   * defaults helper reads, `extensionDecls`), and the project node's own values.
   * A node's values sit on the node, under `extensions`. Absent when the project
   * takes none, so a bundle without them is byte for byte what it was.
   */
  extensions?: Record<string, BundleExtension>;
}

/** One extension as the bundle carries it. */
export interface BundleExtension {
  version: string;
  /** Compiled declarations by node kind: no editor-only fields, no editor words. */
  fields: Record<string, ExtensionFieldDecl[]>;
  /** The project node's values, raw, when it has any. */
  project?: Record<string, unknown>;
}

/**
 * A compiled extension field declaration (`@wildwinter/toolkit/extensions`' FieldDecl,
 * less the editor's words). Its `default` is always present. Structured types
 * carry their parts: `fields` for a record, `key` and `of` for a map, `of` and
 * `id` for a list, `to` for a reference.
 */
export interface ExtensionFieldDecl {
  name: string;
  type: string;
  default: unknown;
  values?: string[];
  stages?: string[];
  to?: string;
  fields?: ExtensionFieldDecl[];
  key?: { type: string; to?: string };
  of?: Omit<ExtensionFieldDecl, "name" | "default"> & { default?: unknown };
  id?: string;
  min?: number;
  max?: number;
  required?: boolean;
}

/** A node's extension values in a bundle: extension id -> field -> value, raw. */
export type NodeExtensions = Record<string, Record<string, unknown>>;

// --- reading the project map (design/project-map-contract.md 5, Q12) ---------
//
// The questions every reader of a bundle asks once a group may live outside the
// box that uses it, answered once here beside `valueAddresses`, because the
// engine, `describeBundle`, the Board and the server each looked a bound group up
// in the hand's own box and silently lost the project one. Pure field access.

/** The tag groups a box sees, by NAME as well as by id: its own groups, then the
 *  project map's group when the box has opted in. The engine resolves a group
 *  name in a box against exactly this list, own groups first (a bundle the
 *  engine loads never has a box group sharing the project group's name, so the
 *  order decides nothing; it is stated for determinism). */
export function groupsOfBox<E>(
  bundle: { map?: { group: TagGroup } },
  box: Pick<Box<E>, "tagGroups" | "usesMap">,
): TagGroup[] {
  return box.usesMap === true && bundle.map !== undefined
    ? [...box.tagGroups, bundle.map.group]
    : box.tagGroups;
}

/** A tag group by internal id, wherever it lives: in a box (`box` set) or on the
 *  project map (`box` absent). Ids are project-unique, so this is bundle-wide;
 *  whether the box asking may USE the group is `groupsOfBox`'s question. */
export function groupById<E>(
  bundle: { boxes: readonly Box<E>[]; map?: { group: TagGroup } },
  id: string,
): { group: TagGroup; box?: Box<E> } | undefined {
  for (const box of bundle.boxes) {
    const group = box.tagGroups.find((g) => g.id === id);
    if (group !== undefined) return { group, box };
  }
  return bundle.map?.group.id === id ? { group: bundle.map.group } : undefined;
}

/** Every tag group in the bundle, each once: the boxes' groups in bundle order,
 *  then the project map's. What a walk over every value bag wants. */
export function allTagGroups<E>(bundle: { boxes: readonly Box<E>[]; map?: { group: TagGroup } }): TagGroup[] {
  const groups = bundle.boxes.flatMap((box) => box.tagGroups);
  return bundle.map !== undefined ? [...groups, bundle.map.group] : groups;
}

/** One declared owned property, as an address prints it. */
export interface PropertyAddress {
  /** The engine address, `<scope>.<owner segment>.<name>`, exactly as
   *  `listProperties` prints it and `setProperty` accepts it. */
  path: string;
  scope: "box" | "deck" | "hand" | "value";
  /** The owner's internal id: what the stores and the save are keyed by. */
  owner: string;
  decl: PropertyDecl;
}

/**
 * Every declared box, deck, hand and tag property in the bundle, by the address
 * a runtime prints for it: boxes in bundle order (the box, its decks, its hands,
 * its tags), then the project map's zones, ONCE, whichever boxes use them. A hand
 * instance carries its template's declarations, as the engine's bags do.
 *
 * The owner segments are `valueAddresses`' for tags, so a zone prints
 * `value.<zone>.<name>` and a repeated box tag its qualified form.
 */
export function propertyAddresses(bundle: {
  boxes: readonly Box<unknown>[];
  map?: { group: TagGroup };
}): PropertyAddress[] {
  const values = valueAddresses(bundle);
  const out: PropertyAddress[] = [];
  const push = (scope: PropertyAddress["scope"], owner: string, segment: string, decls: readonly PropertyDecl[]): void => {
    for (const decl of decls) out.push({ path: `${scope}.${segment}.${decl.name}`, scope, owner, decl });
  };
  const tagsOf = (group: TagGroup): void => {
    for (const tag of group.tags) {
      push("value", tag.id, values.print.get(tag.id) ?? effectiveGameId(tag), tag.properties ?? []);
    }
  };
  for (const box of bundle.boxes) {
    push("box", box.id, effectiveGameId(box), box.properties);
    for (const deck of box.decks) push("deck", deck.id, effectiveGameId(deck), deck.properties);
    for (const hand of box.hands) {
      const decls = hand.template !== undefined
        ? box.handTemplates.find((t) => t.id === hand.template)?.properties ?? []
        : hand.properties ?? [];
      push("hand", hand.id, effectiveGameId(hand), decls);
    }
    for (const group of box.tagGroups) tagsOf(group);
  }
  if (bundle.map !== undefined) tagsOf(bundle.map.group);
  return out;
}

// --- the save envelope --------------------------------------------------------
//
// Version 2 (the one-registry model): property values are the game's
// ScopeRegistry's, not the engine's. The envelope holds what is NOT a property
// (boards, clocks, cooldowns, PRNGs, play logs, spent cards), plus, when the
// engine made its own registry (a standalone game), that registry's values
// under `registry`. A game that passed a registry saves it once itself.
// Version 1 envelopes still load on every runtime: their property partitions
// move into the registry under the keys below.
//
// Registry keys (identical on every runtime, since they are in the save):
// `story` for the shared @story; `storylets/<kind>/<id>` for a shared box,
// deck, hand, or value bag (kind is `box`, `deck`, `hand`, or `value`, id the
// internal id); `storylets/flow/<flowId>/story` and
// `storylets/flow/<flowId>/<kind>/<id>` for a flow's own. Ids escape `%` as
// `%25` and `/` as `%2F`. A bag with no declared properties is not registered.
// A self-backed @world (no resolver bound) is a stored property too, under
// `world`.

/** Version 3 (the hands-on review, 2026-10, foundation 7): a flow's turns,
 *  cooldowns and board, and the shared spent cards, are keyed by GAME ID, as
 *  the play log always was, so a card deleted and made again under the same
 *  Game ID keeps its cooldown and its place as well as its play count. */
export const SAVE_SCHEMA = "storylets/save@3";
/** Version 2, keyed by internal id: still read, every key resolved as a Game
 *  ID first and an internal id second. */
export const SAVE_SCHEMA_V2 = "storylets/save@2";
/** The version 1 envelope's schema tag, still read. */
export const SAVE_SCHEMA_V1 = "storylets/save@1";

export interface PlayRecord {
  /** Card and outcome by gameId (feeds the play-history functions). */
  card: string;
  /** "" for a card with no outcomes, played with none: the key is always
   *  there, so a save's shape does not depend on the card. */
  outcome: string;
  turn: number;
}

/** A property bag: name -> value. */
export type PropertyBag = Record<string, ScalarValue>;

/** The per-scope property partitions one side of the sharing flag holds:
 *  a save carries one of these for the shared values and one per flow
 *  (design/flows.md). NO world key, in either: @world is the game's own
 *  state, resolved through the world resolver and saved by whoever owns
 *  it - "host saves its container once, each engine saves its own
 *  envelope" (engine-runtimes.md 3.1). */
export interface PropsPartition {
  story: PropertyBag;
  box: Record<string, PropertyBag>;
  deck: Record<string, PropertyBag>;
  hand: Record<string, PropertyBag>;
  /** Tag state, keyed by tag id. */
  value: Record<string, PropertyBag>;
}

/** One flow's snapshot inside the envelope (schema 4), and the blob
 *  `saveFlow` parks. */
export interface FlowSave {
  /** The per-flow property partitions. Carried by `saveFlow`, which parks one
   *  flow whole; absent from a version 2 envelope's flows, whose properties
   *  are the registry's. Present in every flow of a version 1 envelope. */
  props?: PropsPartition;
  /** Per-box turn counters, keyed by box Game ID (schema 3.4; by box id
   *  before version 3) - per flow: there is deliberately no global turn. */
  turns: Record<string, number>;
  /** mulberry32 state, uint32 (schema 3.3), per flow. */
  prng: number;
  /** Absolute next-eligible turn (of the card's box's clock) per card Game ID
   *  (card id before version 3); MAX_SAFE_INTEGER = never (deliberately not
   *  Infinity, which JSON-serialises to null). */
  cooldowns: Record<string, number>;
  /** Hand contents (card Game IDs, in dealt order), keyed by hand Game ID
   *  (internal ids before version 3). The claims ledger is derived from this
   *  (schema 3.5). */
  board: Record<string, string[]>;
  playLog: PlayRecord[];
}

/** The whole engine, one envelope: the shared partitions once, then every
 *  live flow keyed by its id - Patter's shape (one shared blob + N flow
 *  blobs; multi-flow and save/load are the same feature). */
/** The engine's half of a save: what every flow shares. Properties, and the
 *  cards a shared `redraw: "never"` has taken out of the world for good
 *  (design/shared-scarcity.md). Claims are NOT here: they are derived from the
 *  live boards, and each flow's board rides its own blob. */
export interface SharedSave {
  /** The shared property partitions: version 1 only. */
  props?: PropsPartition;
  /** Card Game IDs (ids before version 3), sorted, so a save is byte-stable
   *  for a diff. */
  spent: string[];
}

export interface SaveEnvelope {
  /** Written as version 3; a version 2 envelope has the same shape, keyed by
   *  internal id. */
  schema: typeof SAVE_SCHEMA | typeof SAVE_SCHEMA_V2;
  content: BundleContent;
  /** The engine's own registry's values, keyed by registry key: present only
   *  when the engine made the registry itself (a standalone game). A game that
   *  passed a registry saves it once, beside this envelope. */
  registry?: Record<string, PropertyBag>;
  shared: SharedSave;
  flows: Record<string, FlowSave>;
}

/** A version 1 envelope, from before the registry held the properties. Every
 *  runtime still reads it: its partitions move into the registry as it loads. */
export interface SaveEnvelopeV1 {
  schema: typeof SAVE_SCHEMA_V1;
  content: BundleContent;
  shared: SharedSave & { props: PropsPartition };
  flows: Record<string, FlowSave & { props: PropsPartition }>;
}

// --- the load report (design/engine-server.md 4.9) ----------------------------
//
// `loadGame` is forgiving by design: a card the bundle no longer has drops off
// the board, a property the save does not carry keeps its default, and a
// version two builds newer loads without a word. That forgiveness is what makes
// a save survive an edit, and it is also what hides the cost of a content
// update from whoever is about to apply one. The report is the same walk,
// itemised: `previewLoad` computes it and changes nothing, `loadGame` computes
// it and applies it, and `previewFlowRestore` answers the same questions for
// one flow (4.1's `openFlow(id, { restore })`).
//
// Card, hand and flow identities are GAME IDS: a report is host-facing and
// internal ids mean nothing outside the project. An entity the edit DELETED -
// a vanished card, a vanished hand - is named by the key the save itself
// carries, which is its Game ID from save version 3 (an internal id in an
// older save). There is nothing else to name it by.
//
// A property is named differently, and deliberately: by its ENGINE ADDRESS,
// the string listProperties() prints and getProperty()/setProperty() accept.
// A report entry is then something a host can act on rather than merely
// print, and the runtimes have one property grammar instead of two.

/** One card that a restore refused to put back on the board, or a load
 *  dropped from it because it no longer qualifies.
 *
 *  `vanished` and `hand-vanished` are the edit's doing (the card, or the hand
 *  it sat in, is no longer in the bundle). `claimed-elsewhere` is only ever a
 *  single-flow restore into a LIVE engine: the card is shared, and the other
 *  open flows already hold every copy the world has. */
export interface LoadEviction {
  flow: string;
  hand: string;
  card: string;
  /** The rest are a deal's own reasons, met as a load checks what it put
   *  back (save version 3 era; the hands-on review, 2026-10, foundation 7):
   *  the card, its deck or its hand no longer qualifies with the values the
   *  save carries. Checked only when the envelope carries them. */
  reason: "vanished" | "hand-vanished" | "claimed-elsewhere"
    | "condition" | "deck-gate" | "hand-condition" | "cooldown" | "tags" | "taken";
}

/** One property the restore could not put back as it was. `flow` names the
 *  flow whose half it belongs to; absent, it is the shared half.
 *
 *  `path` is the engine's property address, spelled exactly as
 *  `Flow.listProperties()` / `Engine.listProperties()` print it and exactly as
 *  `getProperty` and `setProperty` accept it: `story.<name>` for the story
 *  scope, `<scope>.<ownerGameId>.<name>` for the box, deck, hand and tag
 *  scopes. No `@`, which belongs to the expression language and not to an
 *  address.
 *
 *  The owner segment is its GAMEID (design/engine-server.md 4.4), the name it
 *  is called by everywhere else, so an operator reading a hot-swap report can
 *  paste the address straight into `setProperty`. An owner the build no longer
 *  has keeps the id the save carried: there is no gameId left to give it,
 *  which is the rule the eviction list above has always used. */
export interface LoadProperty {
  flow?: string;
  path: string;
}

/** What a load or a flow restore would do that is not a plain restore
 *  (design/engine-server.md 4.9). Arrays are sorted, so two runtimes given the
 *  same save and bundle produce the same bytes; `flows` alone keeps the
 *  envelope's own order, because a caller re-takes its handles in it. */
export interface LoadReport {
  /** No drift and nothing dropped, defaulted or retyped: the save goes back
   *  exactly as it was. `flows` is not a divergence and does not count. */
  exact: boolean;
  project: string;
  /** Drift when the two differ; reported, never refused. */
  version: { saved: string; bundle: string };
  /** Drift when the two differ; reported, never refused. */
  hash: { saved: string; bundle: string };
  /** The flows this restores, in the order it restores them. */
  flows: string[];
  evicted: LoadEviction[];
  /** Cooldowns held for cards the bundle no longer has. */
  droppedCooldowns: { flow: string; card: string }[];
  /** Shared `redraw: "never"` entries for cards the bundle no longer has. */
  droppedSpent: string[];
  /** In the save, not declared any more. */
  droppedProperties: LoadProperty[];
  /** Declared, not in the save: it takes the declaration's default. */
  defaultedProperties: LoadProperty[];
  /** In the save, still declared, but the saved value no longer fits the
   *  declaration (its type changed, or an enum value / quality stage was
   *  edited away). It takes the declaration's default. */
  retypedProperties: LoadProperty[];
}

/** The .storyletsave FILE: the HOST's file, not the engine's - the engine's
 *  envelope plus, when the host keeps one, its @world container. This is
 *  "host saves its container once, each engine saves its own envelope"
 *  folded into one file for the single-host case; the ENGINE never reads or
 *  writes `world` (loadGame takes the envelope alone). */
export const SAVEFILE_SCHEMA = "storylets/savefile@1";

export interface SaveFile {
  schema: typeof SAVEFILE_SCHEMA;
  /** The engine's envelope: version 2 when written today, version 1 still read. */
  engine: SaveEnvelope | SaveEnvelopeV1;
  /** The host's @world values, saved and restored by the host. */
  world?: PropertyBag;
}

// --- source shards (design/storylets-source.md) --------------------------------

/** The project folder: a macOS package, a plain folder elsewhere. */
export const PROJECT_FOLDER_EXTENSION = ".storylets";
/** The compiled bundle (strict JSON; generated, never hand-edited). */
export const BUNDLE_EXTENSION = ".storyletsc";

/**
 * Where a shipped background sits, relative to the bundle file.
 *
 * One function so the compiler (which writes the name into the bundle) and the
 * export op (which writes the bytes) cannot drift apart: a path agreed in two
 * places is a path that eventually disagrees. One folder for the project, as
 * the source has (`<project>/assets/`), since the project map is the only thing
 * with pictures and there is one of it (design/project-map-contract.md 1.4).
 */
export const bundleAssetPath = (file: string): string => `assets/${file}`;
/** Per-type shard extensions, JSON5 inside (source doc section 2). */
export const SHARD_EXTENSIONS = {
  project: ".storyletproj",
  box: ".storyletbox",
  tags: ".storylettags",
  hands: ".storylethands",
  deck: ".storyletdeck",
  /** The AUTHOR's arrangement layer: the canvases, where cards sit on a deck's
   *  node canvas and the furniture drawn round them. Its own shard because
   *  positions churn (an afternoon of tidying a canvas touches every card) and
   *  content does not, so a designer arranging and a writer editing never
   *  collide on one file (design/graphical-views.md section 1.2). */
  view: ".storyletview",
  /** The DESIGNER's map, in two places told apart by schema tag. At the
   *  project root (`map.storyletmap`, `PROJECTMAP_SCHEMA`) it is the PROJECT
   *  MAP: the zone group and the map's furniture, once for the project
   *  (design/project-map-contract.md 1.1). In a box folder (`MAP_SCHEMA`) it is
   *  that box's SITES: where its hands stand on the project map.
   *
   *  Split out of the view shard on 2026-09-06 (design/engine-server.md 9.1
   *  point 5) because the two halves stopped having one owner. A hand's
   *  position ships in the bundle's `map.geometry` (4.3) and is where a venue's
   *  kiosk stands, so it is SHAPE, which a server's author key may not change;
   *  the canvases are the author's own working drawing and never leave the
   *  project folder. One file could not be both. */
  map: ".storyletmap",
  /** Threaded comments: content-ADJACENT, so neither in a content shard (a
   *  writer's deck edit must not conflict with a reviewer's comment) nor in the
   *  arrangement sidecar (this is not where anything sits). One per box,
   *  id-keyed (design/annotation.md). Documentation NOTES used to share this
   *  file and were retired: `purpose` already says why a thing exists, and
   *  Patterpad's typed routing has no destination here. */
  notes: ".storyletnotes",
  /** An installation contract: what a VENUE depends on, one file per
   *  installation in `contracts/` at the project root
   *  (design/engine-server.md 4.11). Its own shard, and its own folder, for the
   *  walkthrough's reason (Reboot 7.5, S4): a different owner, a different
   *  change rate, and a merge that must never collide with the author's edits,
   *  since the server always wins its own file. */
  contract: ".storyletcontract",
  /** One extension's data (a Storylet extension's, design: patterkit
   *  proposals/patter-storylet-extensions.md): one per extension per box, in the
   *  box folder (`<extension id>.storyletextension`), and one at the project
   *  root for project-level data. Keyed by node kind and id, merged by the
   *  extension's manifest, never interpreted by the engine. Its own shard
   *  because a tool's pass and a writer's edit must not collide on one file. */
  extension: ".storyletextension",
} as const;

/**
 * An extension's manifest, as a project keeps it: `extensions/<id>.extension.json`
 * at the project root (`@wildwinter/toolkit/extensions`' `MANIFEST_FILE_SUFFIX`). Not a
 * shard: JSON the extension ships, never written by the tools nor put in the
 * canonical form, but SOURCE all the same, so a pack carries it and a project
 * compiles from it.
 */
export const MANIFEST_EXTENSION = ".extension.json";
/** Where a project keeps its extensions' manifests, relative to the root. */
export const EXTENSIONS_DIR = "extensions";

/** Every file a project's source is made of: its shards and its manifests. */
export const SOURCE_EXTENSIONS: readonly string[] = [...Object.values(SHARD_EXTENSIONS), MANIFEST_EXTENSION];

/** Where the installation contracts live, relative to the project root. The
 *  directory is the registry, as it is for a box's decks: a contract exists
 *  because its file exists. */
export const CONTRACTS_DIR = "contracts";

export const PROJECT_SCHEMA = "storylets/project@0";
export const BOX_SCHEMA = "storylets/box@0";
export const TAGS_SCHEMA = "storylets/tags@0";
export const HANDS_SCHEMA = "storylets/hands@0";
export const DECK_SCHEMA = "storylets/deck@0";
export const VIEW_SCHEMA = "storylets/view@0";
export const MAP_SCHEMA = "storylets/map@0";
/** The project map shard's schema: the root `map.storyletmap`, beside the
 *  project shard (design/project-map-contract.md 1.1). The same extension as a
 *  box's map shard, told apart by this tag and by sitting at depth one. */
export const PROJECTMAP_SCHEMA = "storylets/projectmap@0";
/** The comment sidecar's schema. Still called "notes" on disk: the file already
 *  held both, and renaming it would break every project for no gain. */
export const NOTES_SCHEMA = "storylets/notes@0";
export const CONTRACT_SCHEMA = "storylets/contract@0";
/** An extension data shard's schema. */
export const EXTENSION_SCHEMA = "storylets/extension@0";

/**
 * One extension's data for one box, or for the project (the root shard).
 *
 * `entries` is the extension's, by node kind (`card`, `outcome`, `box`, `deck`,
 * `tagGroup`, `tag`, `handTemplate`, `hand`, `project`) and then node id, and
 * the canonical form never reorders anything inside it: its lists are the
 * extension's, in the extension's order. The values are raw, as a tool wrote
 * them; the extension's manifest says their types.
 */
export interface ExtensionShard {
  schema: typeof EXTENSION_SCHEMA;
  /** The extension's id: the same as the file's stem. */
  extension: string;
  entries: Record<string, Record<string, Record<string, unknown>>>;
}

/**
 * The extensions a project takes (the project opts in, ruled 2026-10-18), by id:
 * the version it is pinned to. The manifest itself sits in `extensions/`.
 */
export interface ProjectExtension {
  version: string;
}

/**
 * The reserved key a merged shard carries an unresolved merge under, in every
 * shard type (`@wildwinter/toolkit/extensions`' `UNRESOLVED_KEY`): the clashes, each
 * with its path, its kind and the three values, ours being what the shard now
 * holds. Validation refuses a shard carrying it; resolving removes it.
 */
export const UNRESOLVED_KEY = "unresolved";

/**
 * What one installation depends on, written by the venue's server and read by
 * `validate` (design/engine-server.md 4.11).
 *
 * NOT THE AUTHOR'S FILE. A venue is provisioned against names - the hands its
 * stations deal, the boxes its scheduler ticks, the properties its clocks drive,
 * the fields its crew read - and the server writes them out so the tools that
 * already gate a build can refuse a rename before it reaches the venue. A
 * project playing at two venues has two of these. The author never edits one,
 * and today, with no server built, a project either receives one or has none.
 *
 * NEVER COMPILED. It is project-side config like `coverage` and `export`: the
 * server does not need its own contract handed back, it needs the bundle to
 * still honour it.
 *
 * BY GAMEID throughout, because a gameId is the name that crosses the project's
 * border and an internal id is authoring identity.
 */
export interface ContractShard {
  schema: typeof CONTRACT_SCHEMA;
  /** The installation this contract speaks for. One file per installation, and
   *  two files naming the same one is an error. */
  installation: string;
  /** Who wrote it, for a human reading the file ("Storylet Server 0.1.0"). */
  by?: string;
  /** The server's revision when it wrote this. */
  revision?: number;
  /** Hands a station is bound to, by gameId: they may not be renamed or
   *  removed. */
  hands?: string[];
  /** Timed boxes the venue's scheduler ticks, by box gameId, with the turn unit
   *  in SECONDS it was provisioned against. A box whose unit changed means every
   *  rest on its cards changed meaning. */
  boxes?: Record<string, { turn: number }>;
  /** Property paths the venue reads or drives, in the engine's own address
   *  grammar with no `@` ("world.time_wall", "story.visits"), which is how
   *  `listProperties()` prints them. */
  properties?: ContractProperty[];
  /** Card-template field names the crew and the bridges read. */
  fields?: string[];
  /** Outcome field names they read, the same way: the after-line a station
   *  shows when a press lands. What `fields` is to the card template, this is
   *  to the box's `outcomeFields`. */
  outcomeFields?: string[];
}

/**
 * One contracted property.
 *
 * A bare path is the common form and the one the spec's example writes. The
 * object form adds the TYPE the venue was provisioned against, which is the only
 * way `validate` can catch the break that costs a producer most: a property that
 * still exists under the same name and now holds something else. A server that
 * knows the type should write the object form; a hand-written contract may say
 * only the path and get the existence check alone.
 */
export type ContractProperty = string | { path: string; type?: PropertyType };

/** The path a contracted property names, whichever form it was written in. */
export const contractPropertyPath = (p: ContractProperty): string =>
  typeof p === "string" ? p : p.path;

/** The type a contracted property was provisioned against, when it says. */
export const contractPropertyType = (p: ContractProperty): PropertyType | undefined =>
  typeof p === "string" ? undefined : p.type;

/** A point in a canvas's own coordinates. */
export interface ViewPoint {
  x: number;
  y: number;
}

/**
 * Canvas furniture: what an author draws AROUND the content to make sense of it
 * (design/graphical-views.md 3, "Frames and sites").
 *
 * Both canvases carry the same thing, which is why they share a type: a node
 * canvas and a map are different views of different material, but "put a box
 * round this lot and call it act two" is the same thought on either.
 *
 * It lives in the view sidecar because it is ARRANGEMENT. Nothing here is
 * content: no runtime reads it, no bundle carries it, and deleting the sidecar
 * loses only the drawing. Threaded comments are the
 * other thing entirely - they attach to entities and they travel - but a canvas
 * DRAWS their markers, while owning none of them.
 */
export interface CanvasFurniture {
  /** Titled areas behind the content, back to front (see `stacked`).
   *
   *  There was a second kind, a `stickies` list, retired on 2026-08-10
   *  (design/annotation.md): a dropped comment marker does the same job in a
   *  fraction of the space, and an annotation that takes as much room as the
   *  thing it is about is a bad trade on a canvas. */
  frames?: Frame[];
}

/**
 * A titled area behind a group of things: Unreal's comment box.
 *
 * Deliberately dumb about what is inside it. It has no membership list and
 * computes none: a frame is a thing an author DREW, and the cards under it are
 * whatever happens to be under it now. That is what keeps it honest when content
 * moves, and it is the same reasoning that keeps a zone's sites out of the map's
 * sidecar.
 */
export interface Frame extends ViewPoint {
  id: string;
  w: number;
  h: number;
  /** Shown in the frame's bar, and the handle it is dragged by. */
  title?: string;
  /** One of the furniture palette's names (see `FURNITURE_COLOURS`); the theme
   *  decides what that looks like, so a frame does not carry a hex value that
   *  would fight the palette on the day somebody switches theme. */
  colour?: string;
  /** Place in the frame band (sparse, `stacked`). Frames can nest. */
  z?: number;
}

/** The furniture palette: names, not colours. The theme maps them, so the same
 *  shard reads correctly on linen and on baize. */
export const FURNITURE_COLOURS = ["paper", "amber", "sage", "sky", "rose", "slate"] as const;
export type FurnitureColour = typeof FURNITURE_COLOURS[number];

/** One deck's node canvas: where its cards sit, and the furniture around them.
 *  Sparse throughout. A card with no entry lays out by default, and an entry for
 *  a card that no longer exists is inert: kept, and reported by `validate` as a
 *  warning per entry (the family's orphan rule, 2026-10-18), never dropped. */
export interface DeckCanvas extends CanvasFurniture {
  /** Keyed by CARD id. */
  cards?: Record<string, ViewPoint>;
  /** The deck's colour: its stripe on a node canvas and its chip on a card
   *  face. A slot of the 12-colour identity palette (0-11), as a box's
   *  `ViewShard.colour` is. Absent, the colour is a hash of the deck's title,
   *  which is every deck until it is first renamed: the rename stores the
   *  colour the old title had, so the deck keeps it (Ian's ruling,
   *  2026-10-28). Editor-only, like everything in this shard: it never
   *  reaches the bundle. */
  colour?: number;
}

/** The box's map: where its hands stand on the PROJECT map, and nothing else.
 *  Carried by the MAP shard since 2026-09-06; `ViewShard.map` is the old
 *  address, read for one release and never written.
 *
 *  No furniture since the project map (design/project-map-contract.md 1.3): the
 *  frames belong to the one map, in `ProjectMapShard`. A box map shard that
 *  still carries `frames` is warned about and read for nothing until
 *  `storyletengine format` moves them. */
export interface BoxMap {
  /** Keyed by HAND id. WHERE a site is, and nothing else.
   *
   *  Which zone it is IN is not recorded here, and deliberately (2026-08-06,
   *  with the rebinding drag): a hand that binds a zone already says so in its
   *  own shard, as `chosen` or as a rule binding, and that is the truth the
   *  runtime deals from. A copy here could only ever go on to disagree with it,
   *  and a site whose recorded zone contradicts the hand it stands for would be
   *  the most misleading thing on the map.
   *
   *  Called `pins` until 2026-08-10 (design/annotation.md). No compatibility
   *  branch: the only projects that exist are the examples in this repo, and they
   *  were edited. */
  sites?: Record<string, ViewPoint>;
}

/** The AUTHOR's arrangement layer for one box: where cards sit on their decks'
 *  canvases, and the furniture drawn round them.
 *
 *  Its own shard on purpose (design/graphical-views.md section 1.2). Positions
 *  churn, content does not: an afternoon of tidying a canvas touches every card,
 *  and if that lived in the deck shard then a designer arranging and a writer
 *  editing card text would collide on one file all day, while a content review
 *  would be full of coordinates. Keyed by id throughout so the existing merge
 *  engine handles two designers rearranging different things without a conflict.
 *
 *  Source-only. It never reaches the compiled bundle, exactly as `order` does
 *  not: the compiler reads the fields it names and this is not among them. */
export interface ViewShard {
  schema: typeof VIEW_SCHEMA;
  /** Keyed by DECK id: one node canvas each. */
  canvases?: Record<string, DeckCanvas>;
  /** The box's colour on the project map: its layer swatch, its pins and its
   *  chips. A slot of the theme's 12-colour identity palette (0-11), not a hex
   *  value, so it follows light, dark and the colour themes, as a Patter
   *  writing status's `colour` does.
   *
   *  STORED, not computed (the surfacing review's round-3 ruling): a box takes
   *  the first slot no other box on the map uses when it joins, and keeps it
   *  however the boxes are reordered, added or removed, until the author picks
   *  another. Arrangement like everything else here, so it never reaches the
   *  bundle. */
  colour?: number;
  /** @deprecated The box map's old address, kept for one release and READ ONLY.
   *  A reader that meets it uses it when the box has no `MapShard`, and the
   *  formatter moves it; nothing writes it any more. Removed after the next
   *  release, at which point a map left here is simply lost. */
  map?: BoxMap;
}

/** The DESIGNER's map for one box: where its hands stand on the project map.
 *
 *  Split out of the view shard on 2026-09-06 (design/engine-server.md 9.1 point
 *  5). The two halves had stopped sharing an owner: a hand's position ships in
 *  the bundle's `map.geometry.sites` (4.3), which makes it the thing a venue
 *  provisions its kiosks against, while a deck's canvas is a working drawing
 *  that never leaves the folder. A server's author key may change the canvases
 *  and not this.
 *
 *  The map is NESTED under `map` rather than flattened to the top level, and
 *  deliberately: the block's bytes are then exactly what the view shard held, so
 *  the migration is a move of a value rather than a reshaping of it, the merge
 *  strategy carries over word for word, and a reader that has to look in both
 *  places is one expression (`box.map?.map ?? box.view?.map`).
 *
 *  Source-only in the sense the view shard is not: `compileMaps` reads the
 *  positions for the bundle's `map.geometry`, under `export.map`, for a box that
 *  has opted in to the project map. */
export interface MapShard {
  schema: typeof MAP_SCHEMA;
  map: BoxMap;
}

/**
 * The PROJECT MAP's shard: the root `map.storyletmap` (design/project-map-contract.md
 * 1.1). At most one per project, above the boxes.
 *
 * `group` is the zone group, a `TagGroup` byte for byte the shape a box's tags
 * shard holds, so every reader of a group (the spatial marker, polygons,
 * backgrounds, group-level property declarations) carries over unchanged. It is
 * compiled exactly as a box's group is and ships as the bundle's `map.group`. A
 * box opts in with `usesMap` on its box shard; only an opted-in box's cards and
 * hands may reference it.
 *
 * `frames` is the map's furniture, moved here from every box's map shard:
 * arrangement, never compiled. Sites stay per box, in each box's own map shard,
 * because a hand belongs to one box.
 */
export interface ProjectMapShard extends CanvasFurniture {
  schema: typeof PROJECTMAP_SCHEMA;
  group: TagGroup;
}

/** A coverage input driver: during a coverage run the harness feeds a
 *  host-scope property (`@world.x`, `@player.y`) values from `values`, so content gated on
 *  external state gets exercised (Patter's coverageDrivers, carried whole). */
export interface CoverageDriver {
  /** "initial": set once as each playthrough starts. "recurring": re-rolled
   *  per turn at the cadence, so one run passes through several states. */
  kind: "initial" | "recurring";
  /** For recurring drivers: how often to re-roll per turn (default "sometimes"). */
  cadence?: "rarely" | "sometimes" | "often";
  /** The pool the harness picks from (uniform). Empty = inert. */
  values: ScalarValue[];
}

/** Authoring-side coverage configuration (never compiled into the bundle). */
export interface CoverageConfig {
  /** Property drivers, keyed by ref ("@world.danger", "@player.hp"): any host scope's. */
  drivers?: Record<string, CoverageDriver>;

}

/** The version control systems a project can be set up for: `@wildwinter/toolkit/vcs`' list, written out here
 *  because the model takes no tooling dependency (ops checks the two agree). Absent on a project means none. */
export type VcsKind = "git" | "perforce" | "plastic" | "svn";

export interface ProjectShard {
  schema: typeof PROJECT_SCHEMA;
  project: {
    id: string;
    name: string;
    version: string;
  };
  settings: ProjectSettings;
  /** Coverage drivers + argument domains (authoring/testing config; stays
   *  out of the compiled bundle). */
  coverage?: CoverageConfig;
  /** Validation switches (authoring config; never compiled). Off is written
   *  as ABSENT, like `export.map`: a shard says what an author chose. */
  validation?: {
    /** Also warn when state is WRITTEN but nothing reads it. Off by default:
     *  cards are routinely written ahead of the content that will read them,
     *  so mid-development this warning is mostly noise. The read side (a gate
     *  on state nothing writes) always warns, because that kills cards now. */
    warnUnreadWrites?: boolean;
  };
  /**
   * Where the game's shared scopes folder is, relative to the folder holding this file
   * (`"../../shared/game-scopes"`). Authoring config, never compiled, and usually absent: the
   * tools find a `game-scopes/` folder by walking up from the project on their own, stopping at
   * the version-control root, so this is only for a folder that walk would not reach. A path
   * that doesn't exist is a project error. (patterkit design/shared-scopes.md.)
   */
  gameScopes?: string;
  /**
   * The version control system the project is kept in. Authoring config, never compiled. Set, Storyletter
   * talks to that system and no other, rather than guessing from the folders above the project (a project
   * inside an unrelated git checkout would otherwise be staged into it); `"none"` is plain writes, no version
   * control at all. ABSENT is "not chosen", and Storyletter finds the system from the folders as it always
   * did, which is every project made before the setting. Patter's `ProjectFile.vcs`, except that Patter
   * reads absent as none: its projects have had the setting from the start, and ours have not.
   */
  vcs?: VcsKind | "none";
  /**
   * The Patter project this one is paired with: a `.patter` folder, relative to the folder
   * holding this file (`"../story/the-hamlet.patter"`). Authoring config, never compiled: the
   * engines still know nothing of each other (Reboot 10). With it, `validate` checks each card
   * against the scene of the same name in the Patter project's published bundle, and
   * Storyletter can open Patterpad at a card's scene. A folder that isn't there is a warning,
   * since a writer may hold the cards without the dialogue.
   */
  patter?: string;
  /**
   * The boxes the game performs through the paired Patter project, by box id: every card in them
   * plays the scene named after it. Authoring config, never compiled, and only meaningful beside
   * `patter`. With it, `validate` checks only these boxes and also reports a card with no scene;
   * Storyletter's Board plays their cards' scenes, and the playable export carries them.
   * Absent, every card that has a scene is checked and none is required to have one.
   */
  patterBoxes?: string[];
  /**
   * The extensions this project takes, by id (ruled 2026-10-18: the project opts
   * in, a box does not). Authoring config: the bundle carries each one's
   * compiled declarations and values, never this block. An extension named here
   * whose manifest is missing is a warning, and its data is left untouched.
   */
  extensions?: Record<string, ProjectExtension>;
  /**
   * The game's own scopes this project declares (`@world`, `@player`, ...), in
   * Patter's shape: a token, a scope-level `writable` (true when absent), and
   * declarations (absent: an opaque scope, any name, unchecked). Where the game
   * has a scopes folder, `game.scopes.json` is the source and this is the
   * project's synced copy. Absent when the project declares none.
   */
  scopeRegistry?: HostScopeRegistry;
  /** The one game scope a project declared before host scopes: read as the
   *  scope `world` until `storyletengine format` moves it into
   *  `scopeRegistry`. Never written. */
  world?: LegacyWorldBlock;
  story: {
    properties: PropertyDecl[];
  };
  /** Templates of play: configuration bags keyed by template name. Core
   *  validates only what it knows. */
  templates: Record<string, unknown>;
  export: {
    bundle: string;
    metadata: "full" | "stripped";
    /**
     * Does a `.storyletpack` carry the project's binary assets (the project
     * map's background images, in its root `assets/` folder)?
     *
     * Default false, and a project-level DEFAULT rather than a rule: a pack is a
     * delivery, so the caller can override it per pack (2026-08-07). Some
     * projects would benefit from sending their pictures in certain
     * circumstances and others never would, which is why neither "always" nor
     * "never" is the answer.
     *
     * Nothing to do with the compiled bundle, which has its own switch: `map`.
     */
    packAssets?: boolean;
    /**
     * Does the compiled bundle carry the project map's GEOMETRY (zone shapes,
     * background pictures and each opted-in box's sites, `map.geometry`)? The
     * zone group itself always ships when there is a project map: the engine
     * reads it.
     *
     * Default false, and the default matters: geometry is authoring data, the
     * runtime deals in tag names, and a shipping build should carry nothing it
     * does not use. But a host that wants an in-game map should not have to
     * invent its own export, and it is most useful early - a prototype with a
     * real map beats a prototype with a list of zone names.
     *
     * It sits beside `metadata` on purpose: that is already the switch for
     * "authoring data that may or may not ship", and this is its sibling rather
     * than a new concept. With it on, `export` also writes the background files
     * next to the bundle, and `describeBundle` says what is in there.
     */
    map?: boolean;
  };
}

export interface BoxShard {
  schema: typeof BOX_SCHEMA;
  box: {
    id: string;
    gameId?: string;
    title?: string;
    purpose?: string;
    /** Authored display order among boxes (sparse; absent falls back to the
     *  folder-name position). Authoring-only, like a card's (never compiled
     *  into the bundle); merges as a per-field value. */
    order?: number;
    ranking: { specificity: boolean };
    /** Who moves the box's clock (see `Box.turn`): present, the game does,
     *  with `seconds` a timed box; compiled through unchanged. */
    turn?: { seconds?: number };
    /** The Card template's dealing rules: what a card that sets none takes
     *  (the hands-on review, 2026-10). Live, as field defaults are: a card left
     *  on the default follows it, a card's own value stays its own. Absent
     *  keys take the engine's defaults (Redraw "always", weight 1, one slot of
     *  one hand, one hand at a time). */
    cardDefaults?: CardDefaults;
    fields: FieldDecl[];
    /** The outcome fields (see `Box.outcomeFields`); a shard without the key
     *  declares none. */
    outcomeFields?: FieldDecl[];
    properties: PropertyDecl[];
    /** The box has opted in to the PROJECT MAP (design/project-map-contract.md
     *  1.2): its hands may bind the map's zones and its cards may be tagged
     *  with them. Absent is "not on the map"; never written false. Compiled
     *  through to `Box.usesMap`. */
    usesMap?: true;
  };
}

/** The box's OWN tag groups: how its cards are filed. Never the project map's
 *  zone group, which lives in the root map shard and which a box may not
 *  declare a copy of (a spatial group here is a compile error naming
 *  `storyletengine format`). */
export interface TagsShard {
  schema: typeof TAGS_SCHEMA;
  groups: TagGroup[];
}

/** The box's hand templates + hands (the writer/programmer contract). */
export interface HandsShard {
  schema: typeof HANDS_SCHEMA;
  templates: HandTemplate<string>[];
  hands: Hand<string>[];
}

export interface DeckShard {
  schema: typeof DECK_SCHEMA;
  deck: {
    id: string;
    gameId?: string;
    title?: string;
    purpose?: string;
    condition?: string;
    /** Scarce across flows: see Deck.shared. */
    shared?: boolean;
    /** Its `redraw: "never"` cards are spent past the run: see Deck.durable. */
    durable?: boolean;
    /** Authored display order within the box (sparse; see BoxShard). */
    order?: number;
    properties: PropertyDecl[];
  };
  cards: Card<string>[];
}

// --- templates of play --------------------------------------------------------
// The spatial template's types, field access and geometry. Re-exported here so the
// package has one entry point, and kept in its own module because core schema and
// a template of play are different things (Reboot 6).
export * from "./spatial.js";

// How a hand reaches a tag group, and whether that binding is the hand's own to
// change. Core schema rather than a template of play, but the map is what needed
// it said out loud.
export * from "./hands.js";

// Frames: what an author draws around the content. Arrangement,
// so it lives in the sidecar and reads forgivingly (furniture.ts says why).
export * from "./furniture.js";

// Threaded comments: the conversation about a thing, in its own sidecar.
export * from "./comments.js";

// Guessing a property's type from what an outcome writes: the quick fix's input.
export * from "./infer.js";
